import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { FactionSelect } from '../components/ui/FactionSelect';
import { isGameMaster, getNPCStatusClass, getVisibilityClass, getInitials, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  ChevronRight,
  Loader2,
  Eye,
  EyeOff,
  Filter,
  Search,
  LayoutGrid,
  LayoutList,
  Swords,
  Heart,
  Skull,
  HelpCircle,
  Shield
} from 'lucide-react';

const API_URL = '/api';

const NPC_STATUSES = ['alive', 'dead', 'missing', 'hostile', 'ally'];

const STATUS_CONFIG = {
  alive: { icon: Heart, color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/30' },
  dead: { icon: Skull, color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30' },
  missing: { icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/30' },
  hostile: { icon: Swords, color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30' },
  ally: { icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/15 border-blue-500/30' }
};

export default function NPCsPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();

  const [npcs, setNpcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  const [newNpc, setNewNpc] = useState({
    name: '',
    alias: '',
    race: '',
    faction: '',
    role: '',
    public_info: '',
    status: 'alive',
    visibility: 'mj_only'
  });

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    fetchNpcs();
  }, [campaignId]);

  const fetchNpcs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/npcs`);
      setNpcs(response.data);
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newNpc.name.trim()) {
      toast.error(t('error_required', language));
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/npcs`, newNpc);
      toast.success(t('success_saved', language));
      setCreateOpen(false);
      setNewNpc({
        name: '',
        alias: '',
        race: '',
        faction: '',
        role: '',
        public_info: '',
        status: 'alive',
        visibility: 'mj_only'
      });
      navigate(`/campaigns/${campaignId}/npcs/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreating(false);
    }
  };

  const filteredNpcs = npcs.filter(npc => {
    const matchesStatus = filterStatus === 'all' || npc.status === filterStatus;
    const matchesSearch = !searchQuery ||
      npc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      npc.alias?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      npc.faction?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Group by faction
  const npcsByFaction = filteredNpcs.reduce((acc, npc) => {
    const faction = npc.faction || (language === 'fr' ? 'Sans faction' : 'No faction');
    if (!acc[faction]) acc[faction] = [];
    acc[faction].push(npc);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: npcs.length,
    alive: npcs.filter(n => n.status === 'alive').length,
    dead: npcs.filter(n => n.status === 'dead').length,
    hostile: npcs.filter(n => n.status === 'hostile').length,
    ally: npcs.filter(n => n.status === 'ally').length,
  };

  const renderNpcCard = (npc) => {
    const statusConf = STATUS_CONFIG[npc.status] || STATUS_CONFIG.alive;
    const StatusIcon = statusConf.icon;

    return (
      <Card
        key={npc.id}
        className="group glass-card border cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-primary/5 hover:border-primary/40 hover:-translate-y-0.5"
        onClick={() => navigate(`/campaigns/${campaignId}/npcs/${npc.id}`)}
        data-testid={`npc-card-${npc.id}`}
      >
        <CardContent className="p-0">
          {/* Status bar top accent */}
          <div className={cn("h-1 rounded-t-lg", statusConf.bg.replace('bg-', 'bg-').replace('/15', '/40'))} />

          <div className="p-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-14 w-14 ring-2 ring-border/50 group-hover:ring-primary/30 transition-all">
                {npc.image_url && <AvatarImage src={npc.image_url} alt={npc.name} className="object-cover" />}
                <AvatarFallback className="bg-gradient-to-br from-secondary to-secondary/60 text-secondary-foreground font-cinzel text-lg">
                  {getInitials(npc.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="font-cinzel font-bold text-base truncate group-hover:text-primary transition-colors">
                    {npc.name}
                  </h3>
                </div>
                {npc.alias && (
                  <p className="text-sm text-muted-foreground italic truncate">"{npc.alias}"</p>
                )}
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {npc.race && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 font-normal">
                      {npc.race}
                    </Badge>
                  )}
                  {npc.role && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 font-normal">
                      {npc.role}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Footer: status + visibility */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
              <span className={cn(
                "inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border",
                statusConf.bg
              )}>
                <StatusIcon className={cn("h-3 w-3", statusConf.color)} />
                <span className={statusConf.color}>
                  {t(`npc_status_${npc.status}`, language)}
                </span>
              </span>
              {isMJ && (
                <span className={cn(
                  "inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full",
                  npc.visibility === 'mj_only' ? 'bg-magic/10 text-magic' : 'bg-success/10 text-success'
                )}>
                  {npc.visibility === 'mj_only' ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  {t(`visibility_${npc.visibility}`, language)}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderNpcRow = (npc) => {
    const statusConf = STATUS_CONFIG[npc.status] || STATUS_CONFIG.alive;
    const StatusIcon = statusConf.icon;

    return (
      <div
        key={npc.id}
        className="group flex items-center gap-4 px-4 py-3 rounded-lg border border-border/50 bg-card/50 cursor-pointer transition-all duration-200 hover:bg-secondary/30 hover:border-primary/30"
        onClick={() => navigate(`/campaigns/${campaignId}/npcs/${npc.id}`)}
        data-testid={`npc-row-${npc.id}`}
      >
        <Avatar className="h-10 w-10 shrink-0">
          {npc.image_url && <AvatarImage src={npc.image_url} alt={npc.name} className="object-cover" />}
          <AvatarFallback className="bg-gradient-to-br from-secondary to-secondary/60 text-secondary-foreground font-cinzel text-sm">
            {getInitials(npc.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-cinzel font-bold text-sm truncate group-hover:text-primary transition-colors">
              {npc.name}
            </span>
            {npc.alias && (
              <span className="text-xs text-muted-foreground italic truncate hidden sm:inline">"{npc.alias}"</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {npc.race && <span className="text-xs text-muted-foreground">{npc.race}</span>}
            {npc.race && npc.role && <span className="text-xs text-muted-foreground">·</span>}
            {npc.role && <span className="text-xs text-muted-foreground">{npc.role}</span>}
          </div>
        </div>
        <span className={cn(
          "inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border shrink-0",
          statusConf.bg
        )}>
          <StatusIcon className={cn("h-3 w-3", statusConf.color)} />
          <span className={cn(statusConf.color, "hidden sm:inline")}>
            {t(`npc_status_${npc.status}`, language)}
          </span>
        </span>
        {isMJ && (
          <span className={cn(
            "inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full shrink-0",
            npc.visibility === 'mj_only' ? 'bg-magic/10 text-magic' : 'bg-success/10 text-success'
          )}>
            {npc.visibility === 'mj_only' ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in" data-testid="npcs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              {t('npcs', language)}
            </h1>
            <p className="text-muted-foreground mt-1 ml-11">
              {language === 'fr'
                ? 'Gérez les personnages non-joueurs de votre campagne'
                : 'Manage your campaign non-player characters'}
            </p>
          </div>

          {isMJ && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="fantasy-button" data-testid="create-npc-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('npc_create', language)}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <form onSubmit={handleCreate}>
                  <DialogHeader>
                    <DialogTitle className="font-cinzel">{t('npc_create', language)}</DialogTitle>
                    <DialogDescription>
                      {language === 'fr'
                        ? 'Créez un nouveau personnage non-joueur'
                        : 'Create a new non-player character'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="npc-name">{t('npc_name', language)} *</Label>
                        <Input
                          id="npc-name"
                          placeholder={language === 'fr' ? 'Elminster' : 'Elminster'}
                          value={newNpc.name}
                          onChange={(e) => setNewNpc(prev => ({ ...prev, name: e.target.value }))}
                          required
                          data-testid="npc-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="npc-alias">{t('npc_alias', language)}</Label>
                        <Input
                          id="npc-alias"
                          placeholder={language === 'fr' ? 'Le Sage' : 'The Sage'}
                          value={newNpc.alias}
                          onChange={(e) => setNewNpc(prev => ({ ...prev, alias: e.target.value }))}
                          data-testid="npc-alias-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="npc-race">{t('npc_race', language)}</Label>
                        <Input
                          id="npc-race"
                          placeholder={language === 'fr' ? 'Humain' : 'Human'}
                          value={newNpc.race}
                          onChange={(e) => setNewNpc(prev => ({ ...prev, race: e.target.value }))}
                          data-testid="npc-race-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="npc-role">{t('npc_role', language)}</Label>
                        <Input
                          id="npc-role"
                          placeholder={language === 'fr' ? 'Mage, Marchand...' : 'Mage, Merchant...'}
                          value={newNpc.role}
                          onChange={(e) => setNewNpc(prev => ({ ...prev, role: e.target.value }))}
                          data-testid="npc-role-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="npc-faction">{t('npc_faction', language)}</Label>
                      <FactionSelect
                        id="npc-faction"
                        value={newNpc.faction}
                        onChange={(val) => setNewNpc(prev => ({ ...prev, faction: val }))}
                        campaignId={campaignId}
                        language={language}
                        placeholder={language === 'fr' ? 'Les Harpers, La Guilde...' : 'The Harpers, The Guild...'}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="npc-info">{t('npc_public_info', language)}</Label>
                      <Textarea
                        id="npc-info"
                        placeholder={language === 'fr'
                          ? 'Ce que les joueurs savent de ce PNJ...'
                          : 'What players know about this NPC...'}
                        value={newNpc.public_info}
                        onChange={(e) => setNewNpc(prev => ({ ...prev, public_info: e.target.value }))}
                        rows={3}
                        data-testid="npc-info-input"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('npc_status', language)}</Label>
                        <Select value={newNpc.status} onValueChange={(v) => setNewNpc(prev => ({ ...prev, status: v }))}>
                          <SelectTrigger data-testid="npc-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {NPC_STATUSES.map(status => (
                              <SelectItem key={status} value={status}>
                                {t(`npc_status_${status}`, language)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('visibility', language)}</Label>
                        <Select value={newNpc.visibility} onValueChange={(v) => setNewNpc(prev => ({ ...prev, visibility: v }))}>
                          <SelectTrigger data-testid="npc-visibility-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mj_only">{t('visibility_mj_only', language)}</SelectItem>
                            <SelectItem value="players">{t('visibility_players', language)}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                      {t('cancel', language)}
                    </Button>
                    <Button type="submit" disabled={creating} className="fantasy-button" data-testid="create-npc-submit">
                      {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('create', language)}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats bar */}
        {npcs.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: language === 'fr' ? 'Total' : 'Total', value: stats.total, color: 'text-foreground', bg: 'bg-secondary/50' },
              { label: t('npc_status_alive', language), value: stats.alive, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { label: t('npc_status_ally', language), value: stats.ally, color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { label: t('npc_status_hostile', language), value: stats.hostile, color: 'text-red-400', bg: 'bg-red-500/10' },
              { label: t('npc_status_dead', language), value: stats.dead, color: 'text-red-400', bg: 'bg-red-500/10' },
            ].map((stat) => (
              <div key={stat.label} className={cn("rounded-lg px-3 py-2 text-center", stat.bg)}>
                <p className={cn("text-xl font-bold font-cinzel", stat.color)}>{stat.value}</p>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters + View Toggle */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('search', language)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'fr' ? 'Tous les statuts' : 'All statuses'}</SelectItem>
                {NPC_STATUSES.map(status => (
                  <SelectItem key={status} value={status}>
                    {t(`npc_status_${status}`, language)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex rounded-md border border-border overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  "px-2.5 py-2 transition-colors",
                  viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-secondary/50'
                )}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  "px-2.5 py-2 transition-colors",
                  viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-secondary/50'
                )}
              >
                <LayoutList className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* NPCs Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="glass-card overflow-hidden">
                <div className="h-1 bg-muted" />
                <CardContent className="p-4">
                  <div className="animate-pulse space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-14 w-14 bg-muted rounded-full" />
                      <div className="space-y-2 flex-1">
                        <div className="h-5 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                    <div className="h-px bg-muted" />
                    <div className="flex justify-between">
                      <div className="h-5 bg-muted rounded w-16" />
                      <div className="h-5 bg-muted rounded w-20" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : npcs.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-20 text-center">
              <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-secondary/50 mb-6">
                <Users className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="font-cinzel text-xl font-bold mb-2">
                {language === 'fr' ? 'Aucun PNJ' : 'No NPCs'}
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {language === 'fr'
                  ? 'Créez votre premier personnage non-joueur pour peupler votre monde'
                  : 'Create your first non-player character to populate your world'}
              </p>
              {isMJ && (
                <Button onClick={() => setCreateOpen(true)} className="fantasy-button">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('npc_create', language)}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : filteredNpcs.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <Search className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('no_results', language)}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(npcsByFaction).map(([faction, factionNpcs]) => (
              <div key={faction} className="space-y-3">
                <div className="flex items-center gap-3">
                  <h2 className="font-cinzel text-base font-bold text-primary/80">{faction}</h2>
                  <Separator className="flex-1" />
                  <span className="text-xs text-muted-foreground">{factionNpcs.length}</span>
                </div>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {factionNpcs.map(renderNpcCard)}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {factionNpcs.map(renderNpcRow)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
